import { isEmpty } from '@jjj/components';
import divisionActions from './actions';
import { createReducer, getError } from '../../../../helpers/reducer';
import { keyBy } from '../../../../helpers/core';
import { setDefaultCountryField } from '../../../../helpers/countriesHelpers';
import { t } from '../../../../i18n';

const initState = {
  division: null,
  editForm: null,
  isEdit: false,
  loading: false,
  formInfo: {
    countries: [],
  },
  deleted: false,
  filteredUnits: [],
  baseError: null,
};

const initialDivisionData = {
  account_manager: '',
  account_size: 'unknown',
  actions: [],
  activation_date: '',
  activation_rep: '',
  active: 'false',
  active_jobs_count: 0,
  ancestors: [],
  breaks_duration: 0.0,
  breaks_enabled: false,
  child_ids: [],
  city: '',
  company_image_url: '',
  company_logo_url: '',
  cost_center: null,
  country: '',
  future_jobs_count: 0,
  legal_entity_id: '',
  managers: [],
  mobile: '',
  name: '',
  partner: '',
  past_jobs_count: 0,
  sales_rep: '',
  state: '',
  street: '',
  units: [],
  units_count: 0,
  unknown_break_duration: false,
  zip: '',
  booked_tier_pay_rate_enabled: null,
};

function onLoad(state) {
  return {
    ...state,
    loading: true,
    isEdit: false,
  };
}

function onLoadSuccess(state, { payload }) {
  const { data } = payload;
  const division = {
    ...data,
    address: '',
  };

  return {
    ...state,
    division,
    loading: false,
  };
}

function onClearStore() {
  return { ...initState };
}

function validate(attr, value) {
  let error = '';

  if (attr === 'name') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.divisions.division.name_label') });
    }
  }

  if (attr === 'country') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', { fieldName: t('dash.divisions.division.country_title') });
    }
  }

  return error;
}

function onEdit(state, { payload }) {
  const { division } = payload;
  const propsArray = Object.keys(division);
  const unitsProp = prop =>
    prop === 'units'
      ? division[prop].map(item => {
          if (Array.isArray(item)) {
            const [id, name] = item;

            return { value: id, label: name };
          }

          return { value: item.id, label: item.name };
        })
      : division[prop];

  const getFormValue = prop => {
    if (prop === 'state') {
      return division[prop]?.id;
    }

    return division[prop] && Object.prototype.hasOwnProperty.call(division[prop], 'id')
      ? division[prop].id
      : unitsProp(prop);
  };

  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: getFormValue(prop),
      error: prop === 'breaks_duration' && !division.breaks_enabled ? '' : validate(prop, division[prop]),
      touched: false,
    }),
  );

  editForm.active.value = editForm.active.value ? 'true' : 'false';

  return {
    ...state,
    isEdit: true,
    editForm,
  };
}

function onSaveSuccess(state) {
  return {
    ...state,
    division: null,
    isEdit: false,
    editForm: null,
  };
}

function onSaveError(state, { payload }) {
  const { data } = payload;

  const propsArray = Object.entries(state.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        error: getError(entry[0], data),
        touched: true,
      };
    },
  );
  const baseError = data.base ? data.base[0] : null;

  return {
    ...state,
    editForm,
    baseError,
  };
}

function onDeleteSuccess(state) {
  return {
    ...state,
    deleted: true,
  };
}

function onCreate(state) {
  const propsArray = Object.keys(initialDivisionData);
  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: initialDivisionData[prop],
      error: validate(prop, initialDivisionData[prop]),
      touched: false,
    }),
  );

  return {
    ...state,
    isEdit: true,
    division: initialDivisionData,
    editForm: {
      ...editForm,
      country: setDefaultCountryField(),
      active: {
        ...editForm.active,
        value: state.formInfo.activation_statuses[0].id,
      },
      booked_tier_pay_rate_enabled: {
        ...editForm.booked_tier_pay_rate_enabled,
        value: state?.formInfo?.default_booked_tier_pay_rate_enabled || false,
      },
    },
  };
}

function onChangeProperty(state, { payload }) {
  const { attr, value } = payload;
  const { editForm } = state;

  editForm[attr] = {
    value,
    error: validate(attr, value),
    name: attr,
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
    baseError: null,
  };
}

function onChangeProperties(state, { payload }) {
  const { attributes } = payload;
  const propsArray = Object.keys(attributes);

  const editFormProps = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: attributes[prop],
      error: validate(prop, attributes[prop]),
      touched: true,
    }),
  );

  const editForm = {
    ...state.editForm,
    ...editFormProps,
  };

  return {
    ...state,
    editForm,
  };
}

function onCancel(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
  };
}

function onTouch(state, { payload }) {
  const propsArray = Object.entries(payload.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

function onLoadFormDataSuccess(state, { payload }) {
  const { data } = payload;

  return {
    ...state,
    formInfo: {
      ...state.formInfo,
      ...data,
    },
    filteredUnits: [],
  };
}

function onFilterUnitsSuccess(state, { payload }) {
  const { data } = payload;

  return {
    ...state,
    filteredUnits: data,
  };
}

function onChangeSelectedUnits(state, { payload }) {
  const { units } = payload;
  const editForm = { ...state.editForm };

  editForm.units = {
    value: units,
    error: '',
    name: 'units',
    touched: true,
  };

  return {
    ...state,
    editForm,
    baseError: null,
  };
}

function onLoadManagersFormData(state, { payload }) {
  const { data } = payload;
  const administrators = data.map(admin => {
    return {
      id: admin.id,
      name: `${admin.first_name} ${admin.last_name}`,
    };
  });

  return {
    ...state,
    formInfo: {
      ...state.formInfo,
      administrators,
    },
  };
}

export default createReducer(initState, {
  [divisionActions.LOAD]: onLoad,
  [divisionActions.LOAD_SUCCESS]: onLoadSuccess,
  [divisionActions.CLEAR_STORE]: onClearStore,
  [divisionActions.EDIT]: onEdit,
  [divisionActions.CHANGE_PROPERTY]: onChangeProperty,
  [divisionActions.CHANGE_PROPERTIES]: onChangeProperties,
  [divisionActions.CHANGE_SELECTED_UNITS]: onChangeSelectedUnits,
  [divisionActions.CANCEL]: onCancel,
  [divisionActions.SAVE_SUCCESS]: onSaveSuccess,
  [divisionActions.SAVE_ERROR]: onSaveError,
  [divisionActions.DELETE_SUCCESS]: onDeleteSuccess,
  [divisionActions.CREATE]: onCreate,
  [divisionActions.TOUCH]: onTouch,
  [divisionActions.LOAD_FORM_DATA_SUCCESS]: onLoadFormDataSuccess,
  [divisionActions.LOAD_MANAGERS_FORM_DATA]: onLoadManagersFormData,
  [divisionActions.FILTER_UNITS_SUCCESS]: onFilterUnitsSuccess,
});
